import React, { useEffect, useMemo, useRef, useState } from "react";
import Header from "../../master/SideBar";
import Nav from "../../master/NavBar";
import Footer from "../footer";
import { useLocation, useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import Swal from "sweetalert2";
import { getTestDetails, marksExcelUpload } from "../../../api/TestServiceV2";
import TestLayout from "./TestLayout";

const BATCH_SIZE = 5;

/* Header aliases that all mean "registration number" */
const REGISTRATION_ALIASES = [
  "registrationno",
  "registration",
  "rollno",
  "roll",
  "no",
  "srno",
  "sr",
  "srregistrationno",
];

const normalizeKey = (key) => {
  const compact = String(key)
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

  if (REGISTRATION_ALIASES.includes(compact)) return "registration_no";

  return String(key)
    .trim()
    .toLowerCase()
    .replace(/[.,/]+/g, " ")
    .replace(/[^a-z0-9\s]/g, "")
    .replace(/\s+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "");
};

const safeParseSubjects = (raw) => {
  try {
    return JSON.parse(raw || "[]");
  } catch {
    return [];
  }
};

export default function TestExcelMarksUpload() {
  const location = useLocation();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const queryParams = new URLSearchParams(location.search);
  const testId = queryParams.get("id");
  const subjects = safeParseSubjects(queryParams.get("subjects"));

  const [details, setDetails] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [excelData, setExcelData] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [rowErrors, setRowErrors] = useState({});
  const [isUploaded, setIsUploaded] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [dragOver, setDragOver] = useState(false);

  useEffect(() => {
    const loadDetails = async () => {
      try {
        const res = await getTestDetails(testId);
        if (res?.data?.status) setDetails(res.data.data);
      } catch (e) {
        console.error("Unable to load test details", e);
      }
    };
    if (testId) loadDetails();
  }, [testId]);

  /* ------------------------------------------------------------------ */
  /* File reading                                                        */
  /* ------------------------------------------------------------------ */

  const processFile = (file) => {
    if (!file) return;

    if (!/\.(xlsx|xls)$/i.test(file.name)) {
      Swal.fire({
        icon: "info",
        title: "Invalid File",
        text: "Please select an .xlsx or .xls file.",
      });
      return;
    }

    setSelectedFile(file);

    const reader = new FileReader();

    reader.onload = (event) => {
      try {
        const workbook = XLSX.read(event.target.result, { type: "array" });
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];

        // raw: true (default) keeps text cells exactly as typed, so "01234"
        // stays "01234". Numeric cells come back as numbers.
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: "" });

        if (!jsonData.length) {
          Swal.fire({
            icon: "info",
            title: "Empty Excel",
            text: "The file does not contain any data rows.",
          });
          return;
        }

        const formattedData = jsonData.map((row) => {
          const newRow = {};

          Object.entries(row).forEach(([key, value]) => {
            newRow[normalizeKey(key)] = value;
          });

          // Keep registration number exactly as in Excel (leading zeros kept)
          if (newRow.registration_no !== undefined) {
            newRow.registration_no = String(
              newRow.registration_no ?? "",
            ).trim();
          }

          return newRow;
        });

        setExcelData(formattedData);
        setStatuses(Array(formattedData.length).fill("pending"));
        setRowErrors({});
        setIsUploaded(false);
        setProgress(0);
        setFilter("all");
        setSearch("");
      } catch (error) {
        console.error("Excel read error:", error);
        Swal.fire({
          icon: "error",
          title: "Excel Error",
          text: "Unable to read the selected file.",
        });
      }
    };

    reader.readAsArrayBuffer(file);
  };

  const handleFileChange = (e) => processFile(e.target.files?.[0]);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    processFile(e.dataTransfer.files?.[0]);
  };

  const clearFile = () => {
    setSelectedFile(null);
    setExcelData([]);
    setStatuses([]);
    setRowErrors({});
    setIsUploaded(false);
    setProgress(0);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  /* ------------------------------------------------------------------ */
  /* Upload helpers                                                      */
  /* ------------------------------------------------------------------ */

  const getErrorMessage = (result) =>
    result.reason?.response?.data?.message ||
    result.reason?.message ||
    result.value?.data?.message ||
    "Something went wrong";

  /* Upload the given row indexes in batches and update statuses */
  const uploadRows = async (indexes) => {
    const newStatuses = [...statuses];
    const newErrors = { ...rowErrors };

    setProcessing(true);
    setProgress(0);

    let success = 0;
    let failed = 0;

    try {
      for (let i = 0; i < indexes.length; i += BATCH_SIZE) {
        const batch = indexes.slice(i, i + BATCH_SIZE);

        const results = await Promise.allSettled(
          batch.map((rowIndex) =>
            marksExcelUpload({ test_id: testId, ...excelData[rowIndex] }),
          ),
        );

        results.forEach((result, idx) => {
          const rowIndex = batch[idx];

          if (result.status === "fulfilled" && result.value?.data?.status) {
            newStatuses[rowIndex] = "success";
            delete newErrors[rowIndex];
            success++;
          } else {
            newStatuses[rowIndex] = "failed";
            newErrors[rowIndex] = { error: [getErrorMessage(result)] };
            failed++;
          }
        });

        setStatuses([...newStatuses]);
        setRowErrors({ ...newErrors });
        setProgress(Math.round(((i + batch.length) / indexes.length) * 100));

        await new Promise((resolve) => setTimeout(resolve, 300));
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Upload Failed",
        text: error?.message || "Something went wrong",
      });
    } finally {
      setProcessing(false);
    }

    return { success, failed };
  };

  const uploadExcelData = async () => {
    const indexes = excelData.map((_, i) => i);
    const { success, failed } = (await uploadRows(indexes)) || {};

    setIsUploaded(true);

    Swal.fire({
      icon: failed > 0 ? "warning" : "success",
      title: "Upload Completed",
      html: `
        <div class="text-start">
          <p><b>Total Records:</b> ${excelData.length}</p>
          <p><b>Success:</b> ${success ?? 0}</p>
          <p><b>Failed:</b> ${failed ?? 0}</p>
        </div>`,
    });
  };

  const retryFailedRows = async () => {
    const failedIndexes = statuses
      .map((s, i) => (s === "failed" ? i : -1))
      .filter((i) => i !== -1);

    if (!failedIndexes.length) {
      Swal.fire({ icon: "info", title: "No Failed Records Found" });
      return;
    }

    const { success, failed } = (await uploadRows(failedIndexes)) || {};

    Swal.fire({
      icon: failed ? "warning" : "success",
      title: failed ? "Retry Completed" : "All Failed Records Uploaded",
      html: `
        <div class="text-start">
          <p><b>Retried Records:</b> ${failedIndexes.length}</p>
          <p><b>Success:</b> ${success ?? 0}</p>
          ${failed ? `<p><b>Failed:</b> ${failed}</p>` : ""}
        </div>`,
    });
  };

  const retrySingleRow = async (rowIndex) => {
    const result = await uploadRows([rowIndex]);
    if (result?.success) {
      Swal.fire({
        icon: "success",
        title: "Record Uploaded Successfully",
        timer: 1200,
        showConfirmButton: false,
      });
    }
  };

  const updateCell = (rowIndex, key, value) => {
    setExcelData((prev) =>
      prev.map((row, i) => (i === rowIndex ? { ...row, [key]: value } : row)),
    );
  };

  /* ------------------------------------------------------------------ */
  /* Sample download                                                     */
  /* ------------------------------------------------------------------ */

  const downloadSampleExcel = () => {
    const isCombined =
      details?.exam_mode === "combined" || subjects.length === 0;

    const subjectList = details?.subject_details?.length
      ? details.subject_details
      : subjects;

    const students = details?.marks || [];

    let rows = students.map((student) => {
      const row = {
        registration_no: String(
          student?.student_details?.registration_no ?? "",
        ),
      };

      let obtained = [];
      try {
        obtained = JSON.parse(student?.obtained_marks || "[]");
      } catch {
        obtained = [];
      }

      const absent = student?.is_absent === "1";

      if (isCombined) {
        row.combined_marks = absent
          ? "A"
          : (obtained[0] ?? student?.obtained_total ?? "");
      } else {
        const studentSubjects = student?.student_subjects?.length
          ? student.student_subjects
          : subjectList;

        subjectList.forEach((sub) => {
          const idx = studentSubjects.findIndex(
            (s) => String(s?.subject_id) === String(sub?.subject_id),
          );
          row[sub.subject] = absent
            ? "A"
            : idx > -1
              ? (obtained[idx] ?? "")
              : "";
        });
      }

      return row;
    });

    if (!rows.length) {
      const empty = { registration_no: "" };
      if (isCombined) empty.combined_marks = "";
      else subjectList.forEach((s) => (empty[s.subject] = ""));
      rows = [empty];
    }

    const worksheet = XLSX.utils.json_to_sheet(rows);

    // Force the registration column to text so zeros survive editing in Excel
    const range = XLSX.utils.decode_range(worksheet["!ref"]);
    for (let r = range.s.r + 1; r <= range.e.r; r++) {
      const cell = worksheet[XLSX.utils.encode_cell({ r, c: 0 })];
      if (cell) {
        cell.t = "s";
        cell.z = "@";
      }
    }

    worksheet["!cols"] = Object.keys(rows[0]).map((k) => ({
      wch: Math.max(15, k.length + 4),
    }));

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Marks Upload");
    XLSX.writeFile(workbook, `Test_Marks_${details?.title || "Sample"}.xlsx`);
  };

  /* ------------------------------------------------------------------ */
  /* Derived values                                                      */
  /* ------------------------------------------------------------------ */

  const counts = useMemo(() => {
    const c = { success: 0, failed: 0, pending: 0 };
    statuses.forEach((s) => {
      if (c[s] !== undefined) c[s]++;
    });
    return c;
  }, [statuses]);

  const columns = useMemo(
    () => (excelData.length ? Object.keys(excelData[0]) : []),
    [excelData],
  );

  const visibleRows = useMemo(() => {
    const keyword = search.trim().toLowerCase();

    return excelData
      .map((row, index) => ({ row, index }))
      .filter(({ row, index }) => {
        if (filter !== "all" && statuses[index] !== filter) return false;
        if (!keyword) return true;
        return Object.values(row).some((v) =>
          String(v).toLowerCase().includes(keyword),
        );
      });
  }, [excelData, statuses, filter, search]);

  const doneCount = counts.success + counts.failed;
  const donePercent = excelData.length
    ? Math.round((doneCount / excelData.length) * 100)
    : 0;

  const currentStep = !selectedFile ? 1 : isUploaded ? 3 : 2;

  const statusBadge = (status) => {
    if (status === "success")
      return (
        <span className="badge bg-label-success rounded-pill">
          <i className="ri-checkbox-circle-line me-1"></i>Success
        </span>
      );
    if (status === "failed")
      return (
        <span className="badge bg-label-danger rounded-pill">
          <i className="ri-close-circle-line me-1"></i>Failed
        </span>
      );
    return (
      <span className="badge bg-label-warning rounded-pill">
        <i className="ri-time-line me-1"></i>Pending
      </span>
    );
  };

  const steps = [
    { n: 1, label: "Select File", icon: "ri-file-excel-2-line" },
    { n: 2, label: "Review & Upload", icon: "ri-eye-line" },
    { n: 3, label: "Result", icon: "ri-checkbox-circle-line" },
  ];

  /* ------------------------------------------------------------------ */
  /* Render                                                              */
  /* ------------------------------------------------------------------ */

  return (
    <div className="layout-wrapper layout-content-navbar">
      <div className="layout-container">
        <Header />

        <div className="layout-page">
          <Nav />

          <div className="content-wrapper">
            <div className="container-fluid">
              <TestLayout />
              {/* ============ TITLE CARD ============ */}
              <div className="card border-0 shadow-sm rounded-4 mb-4">
                <div className="card-body p-4">
                  <div className="d-flex flex-column flex-lg-row justify-content-between gap-3">
                    <div>
                      <div className="d-flex align-items-center gap-2 mb-2">
                        <span className="badge bg-label-primary rounded-pill px-3 py-2">
                          EXCEL UPLOAD
                        </span>
                        {/* <span className="badge bg-label-secondary rounded-pill px-3 py-2">
                          Test ID: {testId}
                        </span> */}
                        {details?.exam_mode && (
                          <span className="badge bg-label-info rounded-pill px-3 py-2">
                            {details.exam_mode === "combined"
                              ? "Combined"
                              : "Subject Wise"}
                          </span>
                        )}
                      </div>

                      <h4 className="fw-bold mb-1">
                        {details?.title || "Upload Test Marks"}
                      </h4>
                      <p className="text-muted mb-0">
                        Upload marks for all students using an Excel file.
                      </p>
                    </div>

                    <div className="d-flex flex-wrap align-items-start gap-2">
                      <button
                        className="btn btn-outline-secondary"
                        onClick={() => navigate(-1)}
                        disabled={processing}
                      >
                        <i className="ri-arrow-left-line me-1"></i>
                        Back
                      </button>

                      <button
                        className="btn btn-outline-primary"
                        onClick={downloadSampleExcel}
                      >
                        <i className="ri-download-2-line me-1"></i>
                        Download Sample Excel
                      </button>
                    </div>
                  </div>

                  {/* STEPPER */}
                  <div className="d-flex align-items-center mt-4">
                    {steps.map((s, i) => (
                      <React.Fragment key={s.n}>
                        <div className="d-flex align-items-center gap-2">
                          <span
                            className={`avatar-initial rounded-circle d-inline-flex align-items-center justify-content-center ${
                              currentStep >= s.n
                                ? "bg-primary text-white"
                                : "bg-label-secondary"
                            }`}
                            style={{ width: 36, height: 36 }}
                          >
                            <i className={s.icon}></i>
                          </span>
                          <span
                            className={`small ${
                              currentStep >= s.n ? "fw-semibold" : "text-muted"
                            }`}
                          >
                            {s.label}
                          </span>
                        </div>
                        {i < steps.length - 1 && (
                          <div
                            className={`flex-grow-1 mx-3 ${
                              currentStep > s.n
                                ? "bg-primary"
                                : "bg-label-secondary"
                            }`}
                            style={{ height: 2, minWidth: 30 }}
                          ></div>
                        )}
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              </div>

              {/* ============ FILE SELECT ============ */}
              <div className="card border-0 shadow-sm rounded-4 mb-4">
                <div className="card-body p-4">
                  {!selectedFile ? (
                    <div
                      className={`text-center rounded-4 p-5 ${
                        dragOver ? "bg-label-primary" : ""
                      }`}
                      style={{
                        border: "2px dashed var(--bs-primary, #696cff)",
                        cursor: "pointer",
                        transition: "background .15s",
                      }}
                      onClick={() => fileInputRef.current?.click()}
                      onDragOver={(e) => {
                        e.preventDefault();
                        setDragOver(true);
                      }}
                      onDragLeave={() => setDragOver(false)}
                      onDrop={handleDrop}
                    >
                      <i
                        className="ri-upload-cloud-2-line text-primary d-block mb-2"
                        style={{ fontSize: 56 }}
                      ></i>
                      <h5 className="fw-bold mb-1">
                        Drag &amp; drop your Excel file here
                      </h5>
                      <p className="text-muted mb-3">
                        or click to browse (.xlsx, .xls)
                      </p>
                      <small className="text-muted d-block">
                        Tip: download the sample first. It already contains
                        registration numbers and any marks entered so far.
                      </small>
                    </div>
                  ) : (
                    <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
                      <div className="d-flex align-items-center gap-3">
                        <span className="avatar-initial rounded bg-label-success p-3">
                          <i className="ri-file-excel-2-line fs-3"></i>
                        </span>
                        <div>
                          <div className="fw-semibold">{selectedFile.name}</div>
                          <small className="text-muted">
                            {(selectedFile.size / 1024).toFixed(1)} KB
                            {excelData.length > 0 &&
                              ` • ${excelData.length} rows`}
                          </small>
                        </div>
                      </div>

                      <div className="d-flex gap-2">
                        <button
                          className="btn btn-outline-secondary btn-sm"
                          onClick={() => fileInputRef.current?.click()}
                          disabled={processing}
                        >
                          <i className="ri-refresh-line me-1"></i>
                          Change File
                        </button>
                        <button
                          className="btn btn-outline-danger btn-sm"
                          onClick={clearFile}
                          disabled={processing}
                        >
                          <i className="ri-delete-bin-line me-1"></i>
                          Remove
                        </button>
                      </div>
                    </div>
                  )}

                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx,.xls"
                    className="d-none"
                    onChange={handleFileChange}
                  />
                </div>
              </div>

              {/* ============ PREVIEW ============ */}
              {excelData.length > 0 && (
                <div className="card border-0 shadow-sm rounded-4 mb-4">
                  <div className="card-header bg-transparent border-0 p-4 pb-2">
                    {/* STAT TILES */}
                    <div className="row g-3">
                      {[
                        {
                          label: "Total",
                          value: excelData.length,
                          icon: "ri-group-line",
                          color: "primary",
                          key: "all",
                        },
                        {
                          label: "Success",
                          value: counts.success,
                          icon: "ri-checkbox-circle-line",
                          color: "success",
                          key: "success",
                        },
                        {
                          label: "Failed",
                          value: counts.failed,
                          icon: "ri-close-circle-line",
                          color: "danger",
                          key: "failed",
                        },
                        {
                          label: "Pending",
                          value: counts.pending,
                          icon: "ri-time-line",
                          color: "warning",
                          key: "pending",
                        },
                      ].map((t) => (
                        <div className="col-6 col-lg-3" key={t.key}>
                          <div
                            className={`p-3 rounded-3 border h-100 d-flex align-items-center gap-3 ${
                              filter === t.key ? `border-${t.color}` : ""
                            }`}
                            style={{ cursor: "pointer" }}
                            onClick={() => setFilter(t.key)}
                          >
                            <span
                              className={`avatar-initial rounded bg-label-${t.color} d-inline-flex align-items-center justify-content-center`}
                              style={{ width: 44, height: 44 }}
                            >
                              <i className={`${t.icon} fs-4`}></i>
                            </span>
                            <div>
                              <div className="text-muted small">{t.label}</div>
                              <div className="fw-bold fs-5">{t.value}</div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* PROGRESS */}
                    {(processing || doneCount > 0) && (
                      <div className="mt-3">
                        <div className="d-flex justify-content-between small text-muted mb-1">
                          <span>
                            {processing ? "Uploading..." : "Processed"}
                          </span>
                          <span>
                            {doneCount} / {excelData.length} ({donePercent}%)
                          </span>
                        </div>
                        <div className="progress" style={{ height: 8 }}>
                          <div
                            className={`progress-bar ${
                              processing
                                ? "progress-bar-striped progress-bar-animated"
                                : counts.failed
                                  ? "bg-warning"
                                  : "bg-success"
                            }`}
                            style={{ width: `${donePercent}%` }}
                          ></div>
                        </div>
                      </div>
                    )}

                    {/* TOOLBAR */}
                    <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mt-3">
                      <div
                        className="position-relative"
                        style={{ maxWidth: 320, width: "100%" }}
                      >
                        <i
                          className="ri-search-line position-absolute text-muted"
                          style={{
                            left: 12,
                            top: "50%",
                            transform: "translateY(-50%)",
                          }}
                        ></i>
                        <input
                          type="text"
                          className="form-control"
                          style={{ paddingLeft: 36 }}
                          placeholder="Search in rows"
                          value={search}
                          onChange={(e) => setSearch(e.target.value)}
                        />
                      </div>

                      <div className="d-flex gap-2">
                        {!isUploaded ? (
                          <button
                            className="btn btn-success"
                            onClick={uploadExcelData}
                            disabled={processing}
                          >
                            {processing ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-2"></span>
                                Uploading...
                              </>
                            ) : (
                              <>
                                <i className="ri-upload-2-line me-1"></i>
                                Upload Excel
                              </>
                            )}
                          </button>
                        ) : (
                          counts.failed > 0 && (
                            <button
                              className="btn btn-warning"
                              onClick={retryFailedRows}
                              disabled={processing}
                            >
                              {processing ? (
                                <>
                                  <span className="spinner-border spinner-border-sm me-2"></span>
                                  Retrying...
                                </>
                              ) : (
                                <>
                                  <i className="ri-restart-line me-1"></i>
                                  Retry Failed ({counts.failed})
                                </>
                              )}
                            </button>
                          )
                        )}
                      </div>
                    </div>
                  </div>

                  {/* TABLE */}
                  <div className="card-body pt-3 px-0">
                    <div
                      className="table-responsive"
                      style={{ maxHeight: "60vh", overflowY: "auto" }}
                    >
                      <table className="table align-middle mb-0">
                        <thead className="sticky-top" style={{ zIndex: 5 }}>
                          <tr>
                            <th className="text-center" style={{ width: 60 }}>
                              #
                            </th>
                            {columns.map((header) => (
                              <th key={header} className="text-nowrap">
                                {header}
                              </th>
                            ))}
                            <th>Status</th>
                            <th style={{ minWidth: 200 }}>Error</th>
                            <th className="text-center">Action</th>
                          </tr>
                        </thead>

                        <tbody>
                          {visibleRows.length > 0 ? (
                            visibleRows.map(({ row, index }) => (
                              <tr key={index}>
                                <td className="text-center">
                                  <span className="badge bg-label-secondary rounded-circle">
                                    {index + 1}
                                  </span>
                                </td>

                                {columns.map((key) => (
                                  <td key={key}>
                                    {statuses[index] === "failed" ? (
                                      <input
                                        type="text"
                                        className="form-control form-control-sm"
                                        style={{ minWidth: 90 }}
                                        value={row[key] ?? ""}
                                        onChange={(e) =>
                                          updateCell(index, key, e.target.value)
                                        }
                                      />
                                    ) : (
                                      String(row[key] ?? "")
                                    )}
                                  </td>
                                ))}

                                <td>{statusBadge(statuses[index])}</td>

                                <td>
                                  {rowErrors?.[index] ? (
                                    <span className="text-danger small">
                                      {Object.values(rowErrors[index])
                                        .flat()
                                        .join(", ")}
                                    </span>
                                  ) : (
                                    <span className="text-muted">-</span>
                                  )}
                                </td>

                                <td className="text-center">
                                  {statuses[index] === "failed" && (
                                    <button
                                      className="btn btn-outline-warning btn-sm"
                                      onClick={() => retrySingleRow(index)}
                                      disabled={processing}
                                    >
                                      <i className="ri-restart-line me-1"></i>
                                      Retry
                                    </button>
                                  )}
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td
                                colSpan={columns.length + 4}
                                className="text-center py-5"
                              >
                                <div className="text-muted">
                                  <i className="ri-inbox-line fs-1 d-block mb-2"></i>
                                  <div className="fw-semibold">
                                    No rows found
                                  </div>
                                  <small>Try another filter or search.</small>
                                </div>
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <Footer />
          </div>
        </div>
      </div>
    </div>
  );
}
